import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChecklistIcon from "@mui/icons-material/Checklist";
import InboxIcon from "@mui/icons-material/Inbox";
import StoreIcon from "@mui/icons-material/Store";
import PersonIcon from "@mui/icons-material/Person";
import TarjetaPedido from "./TarjetaPedido";
import BarraSeleccionMultiple from "./BarraSeleccionMultiple";

const SIN_SUCURSAL = "Sin sucursal";
const SIN_CHOFER = "Sin chofer asignado";

const GRID_SX = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
  gap: 1,
  alignItems: "stretch",
};

const agruparPor = (pedidos, obtenerClave, etiquetaVacia) => {
  const grupos = new Map();
  pedidos.forEach((pedido) => {
    const clave = obtenerClave(pedido) || etiquetaVacia;
    if (!grupos.has(clave)) grupos.set(clave, []);
    grupos.get(clave).push(pedido);
  });
  return [...grupos.entries()].sort(([a], [b]) => a.localeCompare(b, "es"));
};

const agruparPorSucursal = (pedidos) => agruparPor(pedidos, (p) => p.sucursal_direccion, SIN_SUCURSAL);
const agruparPorChofer = (pedidos) => agruparPor(pedidos, (p) => p.nombre_apellido, SIN_CHOFER);

const GrillaPedidos = ({ pedidos, activeTab, abrirModal, modoSeleccion, seleccionados, toggleSeleccionado }) => (
  <Box sx={GRID_SX}>
    {pedidos.map((pedido) => (
      <TarjetaPedido
        key={pedido.id}
        pedido={pedido}
        activeTab={activeTab}
        abrirModal={abrirModal}
        modoSeleccion={modoSeleccion}
        seleccionado={seleccionados.has(pedido.id)}
        onToggleSeleccion={toggleSeleccionado}
      />
    ))}
  </Box>
);

// Vista única del módulo: tarjetas separadas en secciones por sucursal
// (acordeón colapsable), para que un operador pueda enfocarse en una sola
// sucursal a la vez en lugar de escanear una lista larga mezclada. En la
// pestaña de Asignados, cada sucursal además se subdivide por chofer.
const PedidosPorSucursal = ({ pedidos, loading, activeTab, abrirModal, seleccion, onAsignarGrupo, busqueda }) => {
  const { modoSeleccion, setModoSeleccion, seleccionados, toggleSeleccionado, limpiarSeleccion, alternarGrupo } = seleccion;
  const grupos = useMemo(() => agruparPorSucursal(pedidos), [pedidos]);
  // Todas las sucursales arrancan plegadas; el usuario abre solo las que le interesan.
  const [expandidas, setExpandidas] = useState(new Set());

  useEffect(() => {
    setExpandidas(new Set());
  }, [activeTab]);

  const toggleGrupo = (sucursal) => {
    setExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(sucursal)) next.delete(sucursal);
      else next.add(sucursal);
      return next;
    });
  };

  if (loading && pedidos.length === 0) {
    return (
      <Stack spacing={1.5} sx={{ p: 1.5 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={72} />
        ))}
      </Stack>
    );
  }

  if (pedidos.length === 0) {
    return (
      <Stack alignItems="center" spacing={1} sx={{ py: 6, color: "text.secondary" }}>
        <InboxIcon sx={{ fontSize: 40 }} />
        <Typography variant="body2">
          {busqueda ? `No hay pedidos que coincidan con "${busqueda}"` : "No hay pedidos en este rango de fechas"}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ pb: seleccionados.size > 0 ? 9 : 1.5 }}>
      {activeTab === 0 && (
        <Box sx={{ px: 1.5, pt: 1, display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="small"
            startIcon={<ChecklistIcon fontSize="small" />}
            onClick={() => (modoSeleccion ? limpiarSeleccion() : setModoSeleccion(true))}
          >
            {modoSeleccion ? "Cancelar selección" : "Seleccionar varios"}
          </Button>
        </Box>
      )}

      <Stack spacing={1.5} sx={{ p: 1.5 }}>
        {grupos.map(([sucursal, pedidosSucursal]) => {
          const idsGrupo = pedidosSucursal.map((p) => p.id);
          const todosSeleccionados = idsGrupo.every((id) => seleccionados.has(id));
          const algunoSeleccionado = idsGrupo.some((id) => seleccionados.has(id));

          return (
            <Accordion
              key={sucursal}
              // Buscando, se muestran expandidos los grupos con resultados
              // para no obligar a abrir cada sucursal a mano.
              expanded={busqueda.trim() !== "" || expandidas.has(sucursal)}
              onChange={() => toggleGrupo(sucursal)}
              disableGutters
              variant="outlined"
              sx={{ "&:before": { display: "none" }, borderRadius: 1.5, overflow: "hidden" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "grey.50" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {activeTab === 0 && (
                    <Checkbox
                      size="small"
                      checked={todosSeleccionados}
                      indeterminate={!todosSeleccionados && algunoSeleccionado}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => {
                        if (!modoSeleccion) setModoSeleccion(true);
                        alternarGrupo(idsGrupo);
                      }}
                      sx={{ p: 0.25 }}
                      title={`Seleccionar todos los pedidos de ${sucursal}`}
                    />
                  )}
                  <StoreIcon fontSize="small" color="action" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    {sucursal}
                  </Typography>
                  <Chip size="small" label={pedidosSucursal.length} />
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 1, backgroundColor: "grey.50" }}>
                {activeTab === 1 ? (
                  <Stack spacing={1.5}>
                    {agruparPorChofer(pedidosSucursal).map(([chofer, pedidosChofer]) => (
                      <Box key={chofer}>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                            {chofer}
                          </Typography>
                          <Chip size="small" variant="outlined" label={pedidosChofer.length} />
                        </Stack>
                        <GrillaPedidos
                          pedidos={pedidosChofer}
                          activeTab={activeTab}
                          abrirModal={abrirModal}
                          modoSeleccion={modoSeleccion}
                          seleccionados={seleccionados}
                          toggleSeleccionado={toggleSeleccionado}
                        />
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <GrillaPedidos
                    pedidos={pedidosSucursal}
                    activeTab={activeTab}
                    abrirModal={abrirModal}
                    modoSeleccion={modoSeleccion}
                    seleccionados={seleccionados}
                    toggleSeleccionado={toggleSeleccionado}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>

      <BarraSeleccionMultiple
        cantidad={seleccionados.size}
        onAsignar={() => onAsignarGrupo(pedidos.filter((p) => seleccionados.has(p.id)))}
        onLimpiar={limpiarSeleccion}
      />
    </Box>
  );
};

export default PedidosPorSucursal;
